import { ref, getDownloadURL, listAll, deleteObject } from 'firebase/storage';
import { storage } from './firebase';

/**
 * 指定されたページの静的HTMLファイルのURLを取得します
 * @param {string} slug - ページスラッグ
 * @param {string} subdomain - サブドメイン（オプション）
 * @returns {Promise<string|null>} HTMLファイルのURL、存在しない場合はnull
 */
export const getStaticPageURL = async (slug, subdomain = null) => {
    try {
        const { storage } = await import('./firebase');

        const fileName = subdomain
            ? `subdomains/${subdomain}/pages/${slug}.html`
            : `pages/${slug}.html`;

        console.log('🔍 HTMLファイルURL取得:', { slug, subdomain, fileName });

        const storageRef = ref(storage, fileName);
        const url = await getDownloadURL(storageRef);
        console.log('✅ HTMLファイルURL取得成功:', url);
        return url;
    } catch (error) {
        console.log('❌ HTMLファイルURL取得エラー:', { slug, subdomain, error: error.code, message: error.message });
        // ファイルが存在しない場合はnullを返す
        if (error.code === 'storage/object-not-found') {
            return null;
        }
        console.error('静的HTMLファイルURL取得エラー:', error);
        return null;
    }
};

/**
 * サブドメインの静的HTMLファイルのURLを取得します
 * @param {string} subdomain - サブドメイン
 * @returns {Promise<string|null>} HTMLファイルのURL、存在しない場合はnull
 */
export const getStaticSubdomainURL = async (subdomain) => {
    try {
        const fileName = `subdomains/${subdomain}/index.html`;
        const storageRef = ref(storage, fileName);
        const url = await getDownloadURL(storageRef);
        return url;
    } catch (error) {
        // ファイルが存在しない場合はnullを返す
        if (error.code === 'storage/object-not-found') {
            return null;
        }
        console.error('サブドメイン静的HTMLファイルURL取得エラー:', error);
        return null;
    }
};

/**
 * 指定されたパスの静的HTMLファイルの内容を取得します
 * @param {string} filePath - ファイルパス
 * @returns {Promise<string|null>} HTMLファイルの内容、存在しない場合はnull
 */
export const getStaticHTMLContent = async (filePath) => {
    try {
        console.log('📥 HTMLファイル取得開始:', filePath);
        const response = await fetch(filePath);
        console.log('📊 Fetchレスポンス:', {
            url: filePath,
            status: response.status,
            ok: response.ok,
            statusText: response.statusText
        });

        if (response.ok) {
            const content = await response.text();
            console.log('✅ HTMLファイル取得成功:', content.length, '文字');
            return content;
        }
        console.log('❌ HTMLファイル取得失敗:', response.status, response.statusText);
        return null;
    } catch (error) {
        console.error('❌ 静的HTMLファイル取得エラー:', error);
        return null;
    }
};

/**
 * 静的HTMLファイルが存在するかチェックします
 * @param {string} slug - ページスラッグ
 * @param {string} subdomain - サブドメイン（オプション）
 * @returns {Promise<boolean>} 存在する場合はtrue
 */
export const checkStaticPageExists = async (slug, subdomain = null) => {
    const url = await getStaticPageURL(slug, subdomain);
    return url !== null;
};

/**
 * サブドメインの静的HTMLファイルが存在するかチェックします
 * @param {string} subdomain - サブドメイン
 * @returns {Promise<boolean>} 存在する場合はtrue
 */
export const checkStaticSubdomainExists = async (subdomain) => {
    const url = await getStaticSubdomainURL(subdomain);
    return url !== null;
};

/**
 * 指定されたページの静的HTMLファイルを削除します
 * @param {string} slug - ページスラッグ
 * @param {string} subdomain - サブドメイン（オプション）
 * @returns {Promise<boolean>} 削除成功の場合はtrue
 */
export const deleteStaticPageHTML = async (slug, subdomain = null) => {
    try {
        const { storage } = await import('./firebase');
        const { deleteObject } = await import('firebase/storage');

        const fileName = subdomain
            ? `subdomains/${subdomain}/pages/${slug}.html`
            : `pages/${slug}.html`;

        console.log('🗑️ HTMLファイル削除開始:', { slug, subdomain, fileName });

        const storageRef = ref(storage, fileName);
        await deleteObject(storageRef);
        
        console.log('✅ HTMLファイル削除完了:', fileName);
        return true;
    } catch (error) {
        console.log('❌ HTMLファイル削除エラー:', { slug, subdomain, error: error.code, message: error.message });
        
        // ファイルが存在しない場合はエラーではない
        if (error.code === 'storage/object-not-found') {
            console.log('ℹ️ 削除対象ファイルが存在しませんでした');
            return true;
        }
        
        console.error('静的HTMLファイル削除エラー:', error);
        return false;
    }
}; 